import { Box, Card, CardContent, Stack, Typography } from '@mui/joy';
import { Plus, Trash } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IPartFinderAttributeValue } from 'companySettings/types';
import { Button, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { getEmptyErrorMessage } from 'core/yup';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { RANGE_INCREMENT_FORMAT, RANGE_NUMBER_FORMAT, RANGE_UNIT_MAX_LENGTH } from './constants';
import { buildRangeValue, isRangeInput } from './utils';

const RANGE_FIELDS = ['newRangeMin', 'newRangeMax', 'newRangeUnit', 'newRangeIncrement'];

// Number inputs have no field error of their own, so the message goes in as helper text.
const toHelperTexts = (error?: { message?: unknown }) =>
  error?.message ? [`${error.message}`] : [];

interface IProps {
  hasRanges: boolean;
  onAdd: (value: IPartFinderAttributeValue) => boolean;
}

// One number or range at a time: the card turns into a row on "Add Value" and "+ Add Number &
// Range" opens it again for the next one. Without a max the value is a single number, which has no
// increment.
export const NumberRangeEditor = ({ hasRanges, onAdd }: IProps) => {
  const [isEditing, setIsEditing] = useState(!hasRanges);

  const { t } = useTranslation();

  const { control, errors, getValues, setValue, setError, clearErrors, watch } = useFormContext();

  const hasMin = '' !== `${watch('newRangeMin') ?? ''}`.trim();
  const hasMax = '' !== `${watch('newRangeMax') ?? ''}`.trim();

  const clearRange = () => {
    RANGE_FIELDS.forEach((field) => setValue(field, ''));
    clearErrors(RANGE_FIELDS);
  };

  const onAddRange = () => {
    const min = `${getValues('newRangeMin') ?? ''}`.trim();
    const max = `${getValues('newRangeMax') ?? ''}`.trim();
    const increment = `${getValues('newRangeIncrement') ?? ''}`.trim();
    const unit = `${getValues('newRangeUnit') ?? ''}`.trim();

    if (!min) {
      setError('newRangeMin', {
        type: 'required',
        message: getEmptyErrorMessage(t('labelMin')),
      });

      return;
    }

    // The number inputs let a lone "-" through while a negative number is being typed.
    const invalidNumberField = [
      ['newRangeMin', min],
      ['newRangeMax', max],
      ['newRangeIncrement', increment],
    ].find(([, number]) => number && !Number.isFinite(Number(number)))?.[0];

    if (invalidNumberField) {
      setError(invalidNumberField, {
        type: 'validate',
        message: t('errorPartFinderAttributeRangeNumberInvalid'),
      });

      return;
    }

    if (unit.length > RANGE_UNIT_MAX_LENGTH) {
      setError('newRangeUnit', {
        type: 'maxLength',
        message: t('errorFieldMaxLength', { field: t('labelUnit'), length: RANGE_UNIT_MAX_LENGTH }),
      });

      return;
    }

    if (max && Number(min) > Number(max)) {
      setError('newRangeMax', {
        type: 'validate',
        message: t('errorPartFinderAttributeRangeMinAboveMax'),
      });

      return;
    }

    const isRange = isRangeInput(min, max);

    if (isRange && increment && Number(increment) <= 0) {
      setError('newRangeIncrement', {
        type: 'validate',
        message: t('errorPartFinderAttributeRangeIncrementInvalid'),
      });

      return;
    }

    if (!onAdd(buildRangeValue({ min, max, increment, unit }))) {
      setError(isRange ? 'newRangeMax' : 'newRangeMin', {
        type: 'validate',
        message: t('errorPartFinderAttributeValueExists'),
      });

      return;
    }

    clearRange();
    setIsEditing(false);
  };

  const onDiscardRange = () => {
    clearRange();
    setIsEditing(false);
  };

  if (!isEditing) {
    return (
      <Box>
        <Button
          text={t('buttonLabelAddNumberRange')}
          variant={Variant.SOFT}
          startDecorator={<Plus />}
          onClick={() => setIsEditing(true)}
        />
      </Box>
    );
  }

  return (
    <Card variant={Variant.OUTLINED} color={Color.PRIMARY}>
      <CardContent>
        <Stack gap={2}>
          <Stack direction={Direction.ROW} justifyContent="space-between" alignItems="center">
            <Typography level={Level.TITLE_MD}>{t('labelAttributeTypeNumberRange')}</Typography>
            {hasRanges && (
              <Button
                icon={<Trash />}
                variant={Variant.PLAIN}
                color={Color.DANGER}
                onClick={onDiscardRange}
              />
            )}
          </Stack>

          <Stack direction={Direction.ROW} gap={2}>
            <Box sx={{ flex: 1 }}>
              <FormField
                name="newRangeMin"
                type={InputType.NUMBER_INPUT}
                inputProps={{
                  control,
                  error: errors?.newRangeMin,
                  helperTexts: toHelperTexts(errors?.newRangeMin),
                  label: t('labelMin'),
                  slotProps: { input: RANGE_NUMBER_FORMAT },
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormField
                name="newRangeMax"
                type={InputType.NUMBER_INPUT}
                inputProps={{
                  control,
                  error: errors?.newRangeMax,
                  helperTexts: toHelperTexts(errors?.newRangeMax),
                  label: t('labelMax'),
                  slotProps: { input: RANGE_NUMBER_FORMAT },
                }}
              />
            </Box>
          </Stack>

          <Stack direction={Direction.ROW} gap={2}>
            <Box sx={{ flex: 1 }}>
              <FormField
                name="newRangeUnit"
                type={InputType.TEXT_INPUT}
                inputProps={{
                  control,
                  error: errors?.newRangeUnit,
                  label: t('labelUnit'),
                  placeholder: t('labelUnit'),
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormField
                name="newRangeIncrement"
                type={InputType.NUMBER_INPUT}
                inputProps={{
                  control,
                  error: errors?.newRangeIncrement,
                  helperTexts: toHelperTexts(errors?.newRangeIncrement),
                  label: t('labelIncrement'),
                  disabled: !hasMax,
                  slotProps: { input: RANGE_INCREMENT_FORMAT },
                }}
              />
            </Box>
          </Stack>

          <Box>
            <Button text={t('buttonLabelAddValue')} onClick={onAddRange} disabled={!hasMin} />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
